<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import documentIcon from '@assets/images/propertyDetail/document.svg'
import chevronDownIcon from '@assets/images/propertyDetail/chevron-down.svg'
import downloadIcon from '@assets/images/propertyDetail/download.svg'
import PropertyDetailSection from './PropertyDetailSection.vue'
import {
  groupDocuments,
  type DocumentCategory,
  type DocumentItem,
} from '../utils/propertyDetailUtils'

// DOCUMENTS (Figma 32590:62118) — one collapsible card per category ("Legal
// Documents", "Marketing Documents"); every row is a download link with its
// file type. Cards open by default and toggle independently.

interface Props {
  documents: DocumentItem[]
}

const props = defineProps<Props>()

const groups = computed(() => groupDocuments(props.documents))

const collapsed = ref<Set<DocumentCategory>>(new Set())
watch(
  () => props.documents,
  () => {
    collapsed.value = new Set()
  },
)
const isOpen = (category: DocumentCategory) => !collapsed.value.has(category)
const toggle = (category: DocumentCategory) => {
  const next = new Set(collapsed.value)
  if (next.has(category)) next.delete(category)
  else next.add(category)
  collapsed.value = next
}
</script>

<template>
  <PropertyDetailSection v-if="groups.length" title="Documents">
    <Box
      v-for="group in groups"
      :key="group.category"
      direction="col"
      gap="4"
      class="pd-docs"
      :class="{ 'pd-docs--collapsed': !isOpen(group.category) }"
    >
      <Box
        is="button"
        type="button"
        align="center"
        gap="2"
        class="pd-docs__toggle"
        :aria-expanded="isOpen(group.category)"
        :aria-controls="`pd-docs-${group.category}`"
        @click="toggle(group.category)"
      >
        <AppImage :src="documentIcon" alt="" aria-hidden="true" class="pd-docs__icon-24" />
        <Text
          type="body"
          weight="medium"
          color="var(--p-sliceal-gray-900)"
          is="span"
          class="pd-docs__title"
          style="line-height: 20px"
          >{{ group.title }}</Text
        >
        <AppImage
          :src="chevronDownIcon"
          alt=""
          aria-hidden="true"
          class="pd-docs__icon-24 pd-docs__chevron"
        />
      </Box>

      <Box
        v-if="isOpen(group.category)"
        :id="`pd-docs-${group.category}`"
        is="ul"
        direction="col"
        gap="2"
        class="pd-docs__list"
      >
        <Box v-for="document in group.documents" :key="document.id" is="li" class="pd-docs__item">
          <Box
            is="a"
            :href="document.fileUrl"
            :download="document.fileName"
            target="_blank"
            rel="noopener noreferrer"
            align="center"
            justify="between"
            gap="3"
            class="pd-docs__link"
            :aria-label="`Download ${document.title} (${document.format})`"
          >
            <Text
              type="body"
              color="var(--p-sliceal-gray-600)"
              is="span"
              truncate
              style="line-height: 20px"
            >
              {{ document.title }}
            </Text>
            <Box align="center" gap="2" class="pd-docs__meta">
              <Text
                type="body"
                weight="medium"
                color="var(--p-sliceal-gray-900)"
                is="span"
                style="font-style: italic; line-height: 20px"
                >{{ document.format }}</Text
              >
              <AppImage :src="downloadIcon" alt="" aria-hidden="true" class="pd-docs__icon-20" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </PropertyDetailSection>
</template>

<style scoped>
.pd-docs {
  padding: 16px;
  border: 1px solid var(--p-sliceal-gray-200);
  border-radius: 14px;
  background: var(--p-surface-0);
  overflow: hidden;
}

.pd-docs__toggle {
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.pd-docs__toggle:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 4px;
  border-radius: 4px;
}

.pd-docs__title {
  flex: 1;
  min-width: 0;
}

.pd-docs__icon-24 {
  display: block;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.pd-docs__icon-20 {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

/* The asset points down; an open card shows it flipped (Figma -scale-y). */
.pd-docs__chevron {
  transform: scaleY(-1);
  transition: transform 0.2s ease;
}

.pd-docs--collapsed .pd-docs__chevron {
  transform: none;
}

.pd-docs__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.pd-docs__item {
  padding-bottom: 6px;
  border-bottom: 1px solid var(--p-sliceal-gray-200);
}

.pd-docs__item:last-child {
  padding-bottom: 3px;
  border-bottom: none;
}

.pd-docs__link {
  min-width: 0;
  text-decoration: none;
  border-radius: 4px;
}

.pd-docs__link:hover .pd-docs__icon-20 {
  opacity: 0.8;
}

.pd-docs__link:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.pd-docs__meta {
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .pd-docs__chevron {
    transition: none;
  }
}
</style>
